import { Grid, Card, CardHeader, CardContent, Table, TableHead, TableRow, TableCell, TableBody, Chip } from '@mui/material';
import { Folder, PendingActions, Verified, Block, AccountBalanceWallet, Place } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { PageHeader } from '../../../components/PageHeader';
import { StatCard } from '../../../components/StatCard';
import { MiniBarChart } from '../../../components/MiniBarChart';
import { EmptyState } from '../../../components/EmptyState';
import { projectDashboardApi } from '../../../api/projectOnboarding.api';

const n = (x = 0) => Number(x).toLocaleString('en-IN');

function ChartCard({ title, data }: { title: string; data: Array<{ label: string; value: number }> }) {
  return (
    <Card sx={{ height: '100%' }}>
      <CardHeader title={title} titleTypographyProps={{ variant: 'subtitle1' }} />
      <CardContent>{data?.length ? <MiniBarChart data={data.map((d) => ({ ...d, color: '#1e40af' }))} /> : <EmptyState title="No data yet" dense />}</CardContent>
    </Card>
  );
}

export function ProjectOnboardingDashboardPage() {
  const { data } = useQuery({ queryKey: ['project-onboarding-dashboard'], queryFn: () => projectDashboardApi.dashboard() });
  const k = data?.kpis ?? {};
  const c = data?.charts ?? {};

  return (
    <>
      <PageHeader title="Project Onboarding Dashboard" subtitle="Project pipeline, value, sites and approval analytics." />

      <Grid container spacing={2} mb={1}>
        <Grid item xs={6} sm={4} lg={2}><StatCard gradient color="#1e40af" label="Total Projects" value={n(k.totalProjects)} icon={<Folder />} /></Grid>
        <Grid item xs={6} sm={4} lg={2}><StatCard gradient color="#64748b" label="Draft" value={n(k.draft)} icon={<Folder />} /></Grid>
        <Grid item xs={6} sm={4} lg={2}><StatCard gradient color="#7c3aed" label="Under Review" value={n(k.underReview)} icon={<PendingActions />} /></Grid>
        <Grid item xs={6} sm={4} lg={2}><StatCard gradient color="#16a34a" label="Active Projects" value={n(k.activeProjects)} icon={<Verified />} /></Grid>
        <Grid item xs={6} sm={4} lg={2}><StatCard gradient color="#d97706" label="Delayed" value={n(k.delayedProjects)} icon={<Block />} /></Grid>
        <Grid item xs={6} sm={4} lg={2}><StatCard gradient color="#dc2626" label="Rejected" value={n(k.rejected)} icon={<Block />} /></Grid>
      </Grid>
      <Grid container spacing={2} mb={1}>
        <Grid item xs={6} sm={3}><StatCard color="#1e40af" label="Project Value" value={`₹${n(k.totalProjectValue)}`} icon={<AccountBalanceWallet />} /></Grid>
        <Grid item xs={6} sm={3}><StatCard color="#16a34a" label="Est. Profit" value={`₹${n(k.estimatedProfit)}`} icon={<AccountBalanceWallet />} /></Grid>
        <Grid item xs={6} sm={3}><StatCard color="#0284c7" label="Total Sites" value={n(k.totalSites)} icon={<Place />} /></Grid>
        <Grid item xs={6} sm={3}><StatCard color="#dc2626" label="Pending Payments" value={`₹${n(k.pendingPayments)}`} icon={<AccountBalanceWallet />} /></Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} md={4}><ChartCard title="Applications by Status" data={c.applicationsByStatus ?? []} /></Grid>
        <Grid item xs={12} md={4}><ChartCard title="By Project Type" data={c.projectsByType ?? []} /></Grid>
        <Grid item xs={12} md={4}><ChartCard title="By Business Unit" data={c.projectsByBusinessUnit ?? []} /></Grid>
        <Grid item xs={12} md={4}><ChartCard title="By Billing Type" data={c.projectsByBillingType ?? []} /></Grid>
        <Grid item xs={12} md={4}><ChartCard title="By Zone" data={c.projectsByZone ?? []} /></Grid>
        <Grid item xs={12} md={4}><ChartCard title="Approval TAT" data={c.approvalTAT ?? []} /></Grid>

        <Grid item xs={12} md={6}>
          <Card><CardHeader title="Recently Submitted" titleTypographyProps={{ variant: 'subtitle1' }} />
            <CardContent sx={{ p: 0, overflowX: 'auto' }}>
              {(data?.tables.recentlySubmitted.length ?? 0) === 0 ? <EmptyState title="None" dense /> : (
                <Table size="small"><TableHead><TableRow><TableCell>Application</TableCell><TableCell>Project</TableCell><TableCell>Status</TableCell></TableRow></TableHead>
                  <TableBody>{data!.tables.recentlySubmitted.map((r, i) => <TableRow key={i}><TableCell>{String(r.applicationCode)}</TableCell><TableCell>{String(r.projectName)}</TableCell><TableCell><Chip size="small" label={String(r.status).replace(/_/g, ' ')} /></TableCell></TableRow>)}</TableBody></Table>
              )}
            </CardContent></Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card><CardHeader title="Pending Approvals" titleTypographyProps={{ variant: 'subtitle1' }} />
            <CardContent sx={{ p: 0, overflowX: 'auto' }}>
              {(data?.tables.pendingApprovals.length ?? 0) === 0 ? <EmptyState title="None" dense /> : (
                <Table size="small"><TableHead><TableRow><TableCell>Application</TableCell><TableCell>Project</TableCell><TableCell>Stage</TableCell></TableRow></TableHead>
                  <TableBody>{data!.tables.pendingApprovals.map((r, i) => <TableRow key={i}><TableCell>{String(r.applicationCode)}</TableCell><TableCell>{String(r.projectName)}</TableCell><TableCell>{String(r.stage).replace(/_/g, ' ')}</TableCell></TableRow>)}</TableBody></Table>
              )}
            </CardContent></Card>
        </Grid>
      </Grid>
    </>
  );
}
